import type { Project, Scheduled } from '@finsim/model';
import { CalendarRange } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, EditOnly, Field, IconButton, Tooltip } from '../../../shared/ui';
import { ScheduleEditor } from './schedule-editor';

export interface ScheduledNumberFieldProps {
  label: string;
  value: Scheduled<number>;
  onCommit: (value: Scheduled<number>) => void;
  project: Project;
  /** The single-value input (MoneyField, PercentField…). */
  renderInput: (
    value: number,
    onCommit: (value: number) => void,
    label: string,
    id?: string,
  ) => ReactNode;
  format: (value: number) => string;
}

/** An amount or percentage that can vary over time (amount schedules, formula percentages). */
export function ScheduledNumberField({
  label,
  value,
  onCommit,
  project,
  renderInput,
  format,
}: ScheduledNumberFieldProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const { start, horizonMonths } = project.assumptions;
  return (
    <>
      <Field label={label}>
        {({ id }) =>
          value.kind === 'constant' ? (
            <div className="flex items-start gap-1">
              <div className="min-w-0 flex-1">
                {renderInput(
                  value.value,
                  (v) => {
                    onCommit({ kind: 'constant', value: v });
                  },
                  label,
                  id,
                )}
              </div>
              <EditOnly>
                <Tooltip content={t('schedule.varyOverTime')}>
                  <IconButton
                    label={`${t('schedule.varyOverTime')} — ${label}`}
                    onClick={() => {
                      setEditing(true);
                    }}
                  >
                    <CalendarRange />
                  </IconButton>
                </Tooltip>
              </EditOnly>
            </div>
          ) : (
            <Button
              id={id}
              variant="secondary"
              className="h-auto min-h-7 w-full justify-start py-1 text-left whitespace-normal"
              leadingIcon={<CalendarRange />}
              onClick={() => {
                setEditing(true);
              }}
            >
              <span className="text-xs">
                {t('schedule.summary', {
                  count: value.segments.length,
                  from: format(value.segments[0]?.value ?? 0),
                  to: format(value.segments.at(-1)?.value ?? 0),
                })}
              </span>
            </Button>
          )
        }
      </Field>
      <ScheduleEditor<number>
        open={editing}
        onOpenChange={setEditing}
        title={t('schedule.title', { label })}
        value={value}
        start={start}
        horizon={horizonMonths}
        onSave={onCommit}
        renderValue={(v, onChange, periodLabel) => renderInput(v, onChange, periodLabel)}
        toNumber={(v) => v}
        formatPreview={format}
      />
    </>
  );
}
